import { useCallback, useEffect, useState } from 'react';
import type { AlbumSummary } from '@mantelpiece/shared';
import { api, type StoredSession } from '../lib/api';
import { go } from '../lib/route';
import { Shell } from '../components/Shell';

interface Props {
  readonly session: StoredSession;
  readonly onSignOut: () => void;
}

export const Albums = ({ session, onSignOut }: Props) => {
  const [albums, setAlbums] = useState<readonly AlbumSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [label, setLabel] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setAlbums((await api.listAlbums(session.token)).albums);
      setError(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not load your albums.');
    } finally {
      setLoading(false);
    }
  }, [session.token]);

  useEffect(() => {
    void load();
  }, [load]);

  const create = async () => {
    try {
      const created = await api.createAlbum(session.token, label.trim() || 'New album');
      setLabel('');
      go(`/a/${created.id}`);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not create that album.');
    }
  };

  return (
    <Shell
      title="Albums"
      who={session.account.name}
      onSignOut={onSignOut}
      back={{ label: 'All frames', to: '/' }}
    >
      <div className="stack-loose" style={{ marginTop: '2rem' }}>
        {error && <p className="alert">{error}</p>}

        <div>
          <div className="section-head">
            <p className="eyebrow">Your albums</p>
          </div>
          <p className="note" style={{ marginBottom: '1.25rem' }}>
            An album is a set of photos you keep up to date. Put it on a frame and it stays in
            step: add a picture and it appears on every frame showing the album, take one out and
            it comes down from all of them.
          </p>

          {loading ? (
            <p className="empty">Loading</p>
          ) : albums.length === 0 ? (
            <p className="empty">
              No albums yet. Make one below — it is the easiest way to keep more than one frame
              looking after itself.
            </p>
          ) : (
            <ul className="albums">
              {albums.map((album) => (
                <li key={album.id}>
                  <button type="button" className="album" onClick={() => go(`/a/${album.id}`)}>
                    <span className="album-covers" aria-hidden="true">
                      {album.coverUrls.length === 0 ? (
                        <span className="album-cover album-cover-empty" />
                      ) : (
                        album.coverUrls.map((url) => (
                          <span
                            key={url}
                            className="album-cover"
                            style={{ backgroundImage: `url(${url})` }}
                          />
                        ))
                      )}
                    </span>
                    <span className="album-body">
                      <span className="album-name">{album.label}</span>
                      <span className="ledger-meta">
                        {album.itemCount} photo{album.itemCount === 1 ? '' : 's'}
                        {album.publishedCount > 0
                          ? ` · showing on ${album.publishedCount} frame${
                              album.publishedCount === 1 ? '' : 's'
                            }`
                          : ' · not on a frame yet'}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div>
          <div className="section-head">
            <p className="eyebrow">New album</p>
          </div>
          <div className="row">
            <label className="field">
              <span>What to call it</span>
              <input
                className="input"
                value={label}
                maxLength={40}
                placeholder="Summer at the coast"
                onChange={(event) => setLabel(event.target.value)}
              />
            </label>
            <button type="button" className="button" onClick={() => void create()}>
              Create
            </button>
          </div>
        </div>
      </div>
    </Shell>
  );
};
